import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, TableRow, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Assignment from '@mui/icons-material/Assignment';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Pause from '@mui/icons-material/Pause';
import { TableCellTaskItem, DocumentLinkBox, CategoryChip } from './TaskStyles';
import { IconButton } from '@totalsoft/rocket-ui';
import { getClientCategoryColor } from '../functions';
import { LocalActivity, Star, StarBorder } from '@mui/icons-material';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';

const TaskListItemComponent = ({
  task,
  onAssignToMe,
  internalOnOpenOffer,
  onStartTask,
  onPauseTask,
  onOpenModal,
  onTogglePriority,
  userInfo
}) => {
  const { t } = useTranslation();
  const { dashboardTasksPriority } = useContext(GlobalRightsContext);
  const isAssignedToMe = task.responsible?.id === userInfo.userId;
  const isActiveState = task.status.active;
  const canBeAssignedToMe = task.canBeAssignedToMe && !isAssignedToMe && isActiveState;
  const clientName = task.clientName ? ` / ${task.clientName}` : '';
  const documentTypeName = task.documentType.name ? ` ${task.documentType.name}` : '';

  const canUpdateTaskPriority = useMemo(() => isActiveState && dashboardTasksPriority, [isActiveState, dashboardTasksPriority]);

  const priorityTooltip = useMemo(() => {
    if (!canUpdateTaskPriority) return null;
    return task.isHighPriority ? t('TaskList.UnmarkAsHighPriority') : t('TaskList.MarkAsHighPriority');
  }, [t, task.isHighPriority, canUpdateTaskPriority]);

  return (
    <TableRow>
      <TableCellTaskItem>
        <Grid container alignItems='center' gap={1}>
          <IconButton
            color='warning'
            size='tiny'
            variant='text'
            onClick={onTogglePriority(task)}
            tooltip={priorityTooltip}
            disabled={!canUpdateTaskPriority}
          >
            {task.isHighPriority ? <Star fontSize='small' /> : <StarBorder fontSize='small' />}
          </IconButton>
          <span>{task.name}</span>
        </Grid>
      </TableCellTaskItem>
      <TableCellTaskItem>{t('TaskStatus.' + task.status.name)}</TableCellTaskItem>
      <TableCellTaskItem>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <DocumentLinkBox onClick={internalOnOpenOffer}>
              <strong>
                {documentTypeName} {task.documentNumber}
              </strong>
            </DocumentLinkBox>
            <span> {`${clientName}`}</span>
            {task.clientCategory && (
              <CategoryChip label={task.clientCategory} color={getClientCategoryColor(task.clientCategory)} size='small' />
            )}
          </Grid>
        </Grid>
      </TableCellTaskItem>
      <TableCellTaskItem sx={theme => ({ color: theme.palette.error.main })}>
        <span>
          {task.age.days}d {task.age.hours}h {task.age.minutes}m
        </span>
      </TableCellTaskItem>
      <TableCellTaskItem>
        <Grid container xs='auto' gap={1} alignItems='center'>
          {task.isReplacementTask && task.responsible?.name && (
            <Tooltip title={t('TaskList.TaskByOutOfOffice') + ' ' + task.responsible?.name}>
              <LocalActivity color='primary' fontSize='small' />
            </Tooltip>
          )}
          {task.responsible && task.responsible.name}
        </Grid>
      </TableCellTaskItem>
      <TableCellTaskItem>
        <Grid container gap={1} justifyContent='end'>
          {task.canBeAssignedToOthers && (
            <IconButton color='primary' onClick={onOpenModal(task)} tooltip={t('TaskList.Assign')} size='tiny' variant='text'>
              <MoreHoriz fontSize='small' />
            </IconButton>
          )}
          {canBeAssignedToMe && (
            <IconButton color='primary' onClick={onAssignToMe(task)} tooltip={t('TaskList.AssignToMe')} size='tiny' variant='text'>
              <Assignment fontSize='small' />
            </IconButton>
          )}
          {task.canBeStarted && (
            <IconButton color='primary' onClick={onStartTask(task)} tooltip={t('TaskList.StartWork')} size='tiny' variant='text'>
              <PlayArrow fontSize='small' />
            </IconButton>
          )}
          {task.canBePaused && (
            <IconButton color='primary' onClick={onPauseTask(task)} tooltip={t('TaskList.PauseWork')} size='tiny' variant='text'>
              <Pause fontSize='small' />
            </IconButton>
          )}
        </Grid>
      </TableCellTaskItem>
    </TableRow>
  );
};

TaskListItemComponent.propTypes = {
  task: PropTypes.object.isRequired,
  internalOnOpenOffer: PropTypes.func.isRequired,
  responsibles: PropTypes.array,
  onAssignToMe: PropTypes.func.isRequired,
  onStartTask: PropTypes.func.isRequired,
  onPauseTask: PropTypes.func.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  onTogglePriority: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired
};

export default TaskListItemComponent;
